import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";
import { TranslateService } from "@ngx-translate/core";

import { InterventionDataLookupService } from "./InterventionDataLookupService";
import {
  PrimaryBlotterLookupService,
  ExtendedOrderEntryLogType,
} from "../../impl/PrimaryBlotterLookupService";
import { ModelUpdate } from "../../impl/ModelUpdate";
import { IInterventionDataPromiseLookupService } from "../IInterventionDataPromiseLookupService";
import { ActiveUserProfilePromiseService } from "shared/auth-services";

import { ActiveUser } from "../../../model/core/ActiveUser";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { OrderDataPrivatizationService } from "../../../model/privatization/OrderDataPrivatizationService";
import { DealPrivatizationService } from "../../../model/privatization/DealPrivatizationService";
import { RawDataInterventionAdapter } from "../../../mediator/adapter/RawDataInterventionAdapter";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NotificationService } from "../../notification/impl/NotificationService";
import { BFXNotificationEventType } from "../../../model/core/notifications/BFXNotificationEventType";

import { CubiqWorker } from "../../../worker/CubiqWorker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { StaticDataPrivatizationType } from "shared/cubiq-services/model/privatization/enum/StaticDataPrivatizationType";

const BLOTTERS_INTERVENTION: string[] = ["Default Intervention"]; // Fixing Rate Intervention

@Injectable()
export class InterventionDataPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.InterventionEventImpl>
  implements IInterventionDataPromiseLookupService
{
  private activeUser: ActiveUser | null = null;
  protected orderEntryReferences: Set<string> = new Set();

  // event queuing on prime
  protected queueEvents: boolean = true;
  protected itvnEventQueue: Array<CubiqTS.InterventionEventImpl> = [];

  constructor(
    protected base: InterventionDataLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataInterventionAdapter,
    protected activeUserService: ActiveUserProfilePromiseService,
    _translate: TranslateService,
    private orderPrivatizationService: OrderDataPrivatizationService,
    private dealPrivatizationService: DealPrivatizationService,
    private notificationService: NotificationService,
  ) {
    super(
      base,
      factory,
      CubiqTS.InterventionEventImpl,
      cubiqWorker,
      "InterventionEventImpl",
      CubiqTS.InterventionEventImpl,
      dataAdapter,
      notificationService,
    );

    this.broker.registerMethod(
      "getInterventionData",
      [PRIMITIVE],
      this.getInterventionData.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "addOrderEntryReference",
      [PRIMITIVE],
      this.addOrderEntryReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "removeOrderEntryReference",
      [PRIMITIVE],
      this.removeOrderEntryReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "hasOrderEntryReference",
      [PRIMITIVE],
      this.hasOrderEntryReference.bind(this),
      PRIMITIVE,
    );

    Promise.all([
      this.activeUserService.getActiveUserData(),
      cubiqWorker.isAuth(`${this.constructor.name}`),
      this.orderPrivatizationService.isReady(),
      this.dealPrivatizationService.isReady(),
    ]).then(
      (values) => {
        this.activeUser = values[0];

        _translate.get("aync.prime.i18n").subscribe((i18n: string) => {
          // i18n loader is async. allow loader to complete before adapters execute
          if (i18n !== "aync.prime.i18n") {
            let e = Error(
              "InterventionDataPrimaryLookupService: Failed to prime required translation.",
            );
            this._primed.reject(e);
          }

          let tradeDataObservable = cubiqWorker
            .primeDataProfile(BLOTTERS_INTERVENTION, CubiqTS.InterventionEventImpl.name)
            .subscribe(
              (itvnData: CubiqTS.InterventionEventImpl[]) => {
                this.processInterventionPrime(itvnData);
              },
              (e: Error) => {
                console.debug("Failed to prime intervention data: " + e);
                this._primed.reject(e);
              },
              () => {
                if (this.queueEvents) {
                  this.queueEvents = false;
                  this.flushItvnEventQueue();
                }
                this.base.prime([], true);
                this._primed.resolve(true);
              },
            );
        });
      },
      (reason) => {
        console.debug("InterventionDataPrimaryLookupService: Failed to prime: " + reason);
        this._primed.resolve(false);
      },
    );

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.InterventionEventImpl.name)
      .subscribe((itvnEvent: CubiqTS.InterventionEventImpl) => {
        try {
          if (this.queueEvents && itvnEvent instanceof CubiqTS.InterventionEventImpl) {
            this.itvnEventQueue.push(itvnEvent);
          } else {
            this.processInterventionEvent(itvnEvent);
          }
        } catch (e) {
          logger.out.error("Error occurred processing intervention event: " + e);
        }
      });
  }

  /**
   * Method invoked right after priming is completed in order
   * to apply all intervention update events that have been piled up
   * due to ongoing priming.
   */
  protected flushItvnEventQueue(): void {
    if (this.itvnEventQueue && this.itvnEventQueue.length > 0) {
      logger.out.warn(
        "ITVN -> Flushing " + this.itvnEventQueue.length + " pending intervention updates.",
      );
      this.itvnEventQueue.forEach((itvnEvent: CubiqTS.InterventionEventImpl) => {
        try {
          this.processInterventionEvent(itvnEvent);
        } catch (error) {
          logger.out.error("Failed to process queued intervention event", itvnEvent, error);
        }
      });
      this.itvnEventQueue = [];
    }
  }

  /* ******************************************************* */
  /* INTERVENTION DATA EVENT PROCESSING */

  /*************************************/
  /* PRIME PROCESSING */
  private processInterventionPrime(itvnData: Array<CubiqTS.InterventionEventImpl>): void {
    itvnData = itvnData.filter((itvnDataItem) => {
      if (itvnDataItem instanceof CubiqTS.InterventionEventImpl) {
        if (itvnDataItem.eventType === CubiqTS.InterventionEventType.FIXING_RATE) {
          if (this.isFixingRateVisible(itvnDataItem)) {
            return true;
          }
        } else if (
          itvnDataItem.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING
        ) {
          if (this.isVisible(itvnDataItem)) {
            // If we can see the DI item, let's privatise all the OrderDatas
            const fwdRollData = itvnDataItem.fwdRollOrderData;
            if (
              fwdRollData instanceof CubiqTS.FwdRollOrderDataImpl &&
              fwdRollData.orders?.length > 0
            ) {
              fwdRollData.orders = fwdRollData.orders.map((order) =>
                this.orderPrivatizationService.checkPrivatization(order),
              );
              fwdRollData.clientCd = this.getPrivatisedClientCode(fwdRollData);
              return true;
            }
          }
        } else {
          if (this.isVisible(itvnDataItem)) {
            if (itvnDataItem.orderData && itvnDataItem.orderData instanceof CubiqTS.OrderDataImpl)
              itvnDataItem.orderData = this.orderPrivatizationService.checkPrivatization(
                itvnDataItem.orderData as CubiqTS.OrderDataImpl,
              );

            if (itvnDataItem.deal && itvnDataItem.orderData instanceof CubiqTS.DealImpl)
              itvnDataItem.deal = this.dealPrivatizationService.checkPrivatization(
                itvnDataItem.deal as CubiqTS.DealImpl,
              );

            return true;
          }
        }
      }
      return false;
    });

    this.base.prime(itvnData, false);
  }

  /*************************************/
  /* RUN-TIME EVENT PROCESSING */
  private processInterventionEvent(itvnEvent: CubiqTS.InterventionEventImpl): void {
    // handle fixing rate
    if (
      itvnEvent instanceof CubiqTS.InterventionEventImpl &&
      itvnEvent.eventType === CubiqTS.InterventionEventType.FIXING_RATE
    ) {
      // delete
      if (itvnEvent.operationType === CubiqTS.InterventionOperationType.CLEAR) {
        this.fireModelDeleteEvent(itvnEvent.eventId, true);
      } else if (itvnEvent.operationType === CubiqTS.InterventionOperationType.NEW) {
        // update
        if (this.isFixingRateVisible(itvnEvent)) {
          this.base.mapItem(itvnEvent);
          this.fireModelUpdateEvent(itvnEvent);
          this.processNotification(itvnEvent);
        }
      }
      return;
    }

    if (
      // cleanup after successful Fill Cancel or Fill Clear
      (itvnEvent.eventType === CubiqTS.InterventionEventType.FILL ||
        itvnEvent.eventType === CubiqTS.InterventionEventType.FWD_FILL_PENDING ||
        itvnEvent.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING) &&
      itvnEvent.operationType === CubiqTS.InterventionOperationType.CLEAR
    ) {
      this.fireModelDeleteEvent(itvnEvent.eventId);

      if (
        itvnEvent != null &&
        itvnEvent.orderData != null &&
        CubiqTS.Long.isLong(itvnEvent.orderData.orderId)
      ) {
        this.fireModelNotificationClearEvent([itvnEvent.orderData.orderId.toString()], true);
      }

      return;
    }

    if (itvnEvent.orderData && itvnEvent.orderData instanceof CubiqTS.OrderDataImpl) {
      itvnEvent.orderData = this.orderPrivatizationService.checkPrivatization(
        itvnEvent.orderData as CubiqTS.OrderDataImpl,
      );
    }
    if (itvnEvent.fwdRollOrderData?.orders?.length > 0) {
      for (const order of itvnEvent.fwdRollOrderData.orders) {
        if (order instanceof CubiqTS.OrderDataImpl) {
          this.orderPrivatizationService.checkPrivatization(order);
        }
      }
      itvnEvent.fwdRollOrderData.clientCd = this.getPrivatisedClientCode(
        itvnEvent.fwdRollOrderData,
      );
    }
    if (itvnEvent.deal && itvnEvent.orderData instanceof CubiqTS.DealImpl) {
      itvnEvent.deal = this.dealPrivatizationService.checkPrivatization(
        itvnEvent.deal as CubiqTS.DealImpl,
      );
    }

    //  log
    this.logOrderEntryDebugInfo(itvnEvent);
    // mirrors the flow below for order entry
    this.processAndFireOrderEntryEvent(itvnEvent);

    // if user is trader and event is limit order (interventionEventType) send to ModelOrderEntryEvent subject
    if (
      this.activeUser.isTrader() &&
      itvnEvent.eventType === CubiqTS.InterventionEventType.LIMIT_ORDER
    ) {
      return;
    }

    /**
     * This check was added since sales user can not to Bmark Acceptance, which in turn is received on approved Credit Entry Fail or Force to DI
     * Thus this fixes the bug where the order remains in DI for a sales user after it already approved Entry Fail.
     */
    let isSalesBmarkAcceptance: boolean =
      CubiqTS.InterventionOperationType.NEW == itvnEvent.operationType &&
      this.activeUser.isSales() &&
      itvnEvent.orderData instanceof CubiqTS.OrderDataImpl &&
      itvnEvent.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
      itvnEvent.orderData.orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED;

    if (
      CubiqTS.InterventionOperationType.APPROVE == itvnEvent.operationType ||
      CubiqTS.InterventionOperationType.REJECT == itvnEvent.operationType ||
      isSalesBmarkAcceptance
    ) {
      this.fireModelDeleteEvent(itvnEvent.eventId);

      if (
        itvnEvent != null &&
        itvnEvent.orderData != null &&
        CubiqTS.Long.isLong(itvnEvent.orderData.orderId)
      ) {
        this.fireModelNotificationClearEvent([itvnEvent.orderData.orderId.toString()], true);
      }
      // removal does not require a visibility check
      return;
    }

    if (!this.isVisible(itvnEvent)) {
      logger.out.info(
        `${this.constructor.name}: filtering invisible intervention event ${itvnEvent.eventId}`,
      );

      // for example FILL has a successful auto-steal
      let existingKey = this.base.getItemKey(itvnEvent);
      if (existingKey != null) {
        let existingItem = this.base.getItem(existingKey);

        if (existingItem != null && existingItem.eventId != null) {
          this.fireModelDeleteEvent(existingItem.eventId);
        }

        if (
          existingItem != null &&
          existingItem.orderData != null &&
          CubiqTS.Long.isLong(existingItem.orderData.orderId)
        ) {
          this.fireModelNotificationClearEvent([existingItem.orderData.orderId.toString()]);
        }

        this.base.deleteItem(existingItem, existingKey);
      }
      return;
    }

    // eligible for InterventionDataPrimaryLookupService adapt
    if (itvnEvent.operationType === CubiqTS.InterventionOperationType.NEW) {
      this.base.mapItem(itvnEvent);
      this.fireModelUpdateEvent(itvnEvent);

      const { notifType, notifSubType } = this.processNotification(itvnEvent);

      this.logOtherDebugInfo(
        `NOTIFY >>> INTERVENTION Event (msgID: ${itvnEvent.messageId}) => [${
          itvnEvent.orderData != null ? itvnEvent.orderData.clOrdID : "N/A"
        }] | Notif. type: ${notifType} [${
          BFXNotificationType[notifType]
        }] | Notif. sub-type: ${notifSubType} [${
          BFXNotificationSubType[notifSubType]
        }] | Should send : ${
          this.notificationService.shouldNotify(BFXNotificationType.DEALER_INTERVENTION)
            ? "YES"
            : "NO"
        } `,
        ` | lock user: ${
          itvnEvent.lockUserId != null ? itvnEvent.lockUserId : "N/A"
        } | entry user: ${
          itvnEvent.orderData != null ? itvnEvent.orderData.entryUserId.toString() : "N/A"
        } | active user: ${this.activeUser.userId.toString()} | users comparison ${
          itvnEvent.orderData != null
            ? itvnEvent.orderData.entryUserId.toString() == this.activeUser.userId
              ? "EQUAL"
              : "NOT EQUAL"
            : "Entry User NULL"
        }`,
      );
    } else {
      logger.out.error(
        `ERROR in InterventionDataPrimaryLookupService: unknown operation type: "${itvnEvent.operationType}" for intervention event update `,
      );
    }
  }

  private processNotification(itvnEvent: CubiqTS.InterventionEventImpl) {
    let notifType: BFXNotificationType = null;
    let notifSubType: BFXNotificationSubType = null;
    let notificationEvent = null;
    let isClear = false;
    let eventType = BFXNotificationEventType.INTERVENTION_UPDATE_EVENT;

    if (this.notificationService.shouldNotify(BFXNotificationType.DEALER_INTERVENTION)) {
      let orderEntryUserIdNumber =
        itvnEvent.orderData != null ? itvnEvent.orderData.entryUserId.toString() : null;
      notifType = this.convertEventTypeToNotifType(itvnEvent.operationType);
      notifSubType = this.convertEventTypeToSubType(itvnEvent.reason);

      if (this.isInterventionNotification(notifSubType, itvnEvent, orderEntryUserIdNumber)) {
        notificationEvent = this.notificationService.interventionDataToNotification(
          itvnEvent,
          notifType,
          notifSubType,
        );
      } else if (this.isFwdRollPriceNotification(notifSubType, itvnEvent)) {
        notificationEvent = this.notificationService.fwdRollDataToNotification(
          itvnEvent,
          notifType,
          notifSubType,
        );
        eventType = BFXNotificationEventType.FWD_ROLL_PRICE;
      } else if (this.isInterventionFixingRateRequirementAltNotification(notifSubType, itvnEvent)) {
        notificationEvent =
          this.notificationService.interventionFixingRateRequirementAltToNotification(
            itvnEvent,
            notifType,
            notifSubType,
          );
        eventType = BFXNotificationEventType.FIXING_RATE_REQUIREMENT;
      } else if (this.isInterventionClearEvent(itvnEvent)) {
        notificationEvent = this.notifService.dataToClearEvent([itvnEvent.orderData.orderId]);
        isClear = true;
      }
    }

    if (notificationEvent != null) {
      if (isClear) {
        this.notificationService.fireNotificationClearEvent(notificationEvent, eventType);
      } else {
        this.notificationService.fireNotificationEvent(notificationEvent, eventType);
      }
    }

    return { notifSubType, notifType };
  }

  private isInterventionNotification(
    notifSubType: BFXNotificationSubType,
    itvnEvent: CubiqTS.InterventionEventImpl,
    orderEntryUserIdNumber: string,
  ) {
    return (
      (notifSubType == BFXNotificationSubType.CREDIT_FAIL_ENTRY ||
        notifSubType == BFXNotificationSubType.MODIFY_FAIL_ENTRY ||
        notifSubType == BFXNotificationSubType.CREDIT_FAIL_FORCE_DI) &&
      itvnEvent.lockUserId == null &&
      orderEntryUserIdNumber !== this.activeUser.userId
    );
  }

  private isFwdRollPriceNotification(
    notifSubType: BFXNotificationSubType,
    itvnEvent: CubiqTS.InterventionEventImpl,
  ) {
    return (
      notifSubType == BFXNotificationSubType.FWD_ROLL_PRICE &&
      itvnEvent.lockUserId == null &&
      itvnEvent.unLockUserId == null
    );
  }

  private isInterventionFixingRateRequirementAltNotification(
    notifSubType: BFXNotificationSubType,
    itvnEvent: CubiqTS.InterventionEventImpl,
  ) {
    return (
      BFXNotificationSubType.FIXING_RATE_REQUIREMENT === notifSubType &&
      itvnEvent.fixingRateRequirement != null &&
      itvnEvent.fixingRateRequirement.commentsTxt != null &&
      Array.isArray(itvnEvent.fixingRateRequirement.commentsTxt.errorMessages) &&
      itvnEvent.fixingRateRequirement.commentsTxt.errorMessages
        .map((value) => {
          try {
            return JSON.parse(value as any);
          } catch (e) {
            return null;
          }
        })
        .some((parsed) => {
          return (
            parsed != null &&
            typeof parsed === "object" &&
            parsed.id === "di.txn.bm.fixing.rate.req.alt"
          );
        })
    );
  }

  private isInterventionClearEvent(itvnEvent: CubiqTS.InterventionEventImpl) {
    return itvnEvent.orderData != null && CubiqTS.Long.isLong(itvnEvent.orderData.orderId);
  }

  /*************************************/
  /* ORDER-ENTRY EVENT PROCESSING */
  private processAndFireOrderEntryEvent(interventionEvent: CubiqTS.InterventionEventImpl) {
    if (interventionEvent instanceof CubiqTS.InterventionEventImpl) {
      let refId: string =
        interventionEvent.orderData != null ? interventionEvent.orderData.clOrdID : null;
      if (refId != null && this.orderEntryReferences.has(refId)) {
        let isBenchmark: boolean =
          interventionEvent.orderData != null
            ? interventionEvent.orderData.orderType === CubiqTS.OrderType.BENCHMARK
            : false;
        let metaData: any = {
          isBenchmark: isBenchmark,
          orderId: interventionEvent.orderData.orderId,
          orderRef: interventionEvent.orderData.orderRef,
          ref: interventionEvent.orderData.clOrdID,
          state: null,
          eventType: null,
          tgcId: null,
          sgcId: null,
          user: null,
          invEvtType: interventionEvent.eventType,
          invOprType: interventionEvent.operationType,
          reasonId: interventionEvent.reason,
          unlockUserId:
            interventionEvent.unLockUserId != null
              ? interventionEvent.unLockUserId.toString()
              : null,
          messageId: interventionEvent.messageId,
          errors: interventionEvent.errors,
        };

        // augment metadata
        let traderGroupCodeId =
          interventionEvent.orderData != null && interventionEvent.orderData.deskId != null
            ? interventionEvent.orderData.deskId.toString()
            : null;
        if (
          interventionEvent.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
          interventionEvent.orderData &&
          Array.isArray(interventionEvent.orderData.allocationData) &&
          interventionEvent.orderData.allocationData.length > 0
        ) {
          // first allocation TG
          let deskId: CubiqTS.Long = interventionEvent.orderData.allocationData[0].deskId;
          if (deskId != null) {
            traderGroupCodeId = deskId.toString();
          }
        }
        let salesGroupCodeId =
          interventionEvent.orderData != null && interventionEvent.orderData.salesGroupId != null
            ? interventionEvent.orderData.salesGroupId.toString()
            : null;
        let lockUser =
          interventionEvent.lockUserId != null ? interventionEvent.lockUserId.toString() : null;

        if (
          this.activeUser.isTrader() &&
          interventionEvent.eventType === CubiqTS.InterventionEventType.LIMIT_ORDER
        ) {
          metaData.tgcId = traderGroupCodeId;
          metaData.sgcId = salesGroupCodeId;
          metaData.user = lockUser;
        } else if (interventionEvent.operationType === CubiqTS.InterventionOperationType.NEW) {
          if (!this.isVisible(interventionEvent)) {
            this.logOrderEntryDebugInfo(
              interventionEvent,
              "Not visible",
              ExtendedOrderEntryLogType.FORWARD_IGNORE_SIMPLE,
            );
            return;
          }
          metaData.state = interventionEvent.operationType;
          metaData.tgcId = traderGroupCodeId;
          metaData.sgcId = salesGroupCodeId;
          metaData.user = lockUser;
        } else if (
          interventionEvent.operationType === CubiqTS.InterventionOperationType.APPROVE ||
          interventionEvent.operationType === CubiqTS.InterventionOperationType.REJECT
        ) {
          // do not augment metadata
        }
        this.fireModelOrderEntryEvent(metaData);
      }
    }
  }

  /* ******************************************************* */
  /* PUBLIC METHODS */

  public getInterventionData(id: string): Promise<CubiqTS.InterventionEventImpl> {
    return new Promise<CubiqTS.InterventionEventImpl>((res, rej) => {
      res(this.base.getInterventionData(id));
    });
  }

  /*************************************/
  /* ORDER-ENTRY Reference Management */
  public addOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (!this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.add(ref);
        this.logOrderEntryDebugInfo(ref, "Intervention", ExtendedOrderEntryLogType.LISTENER_ADD);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public removeOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.delete(ref);
        this.logOrderEntryDebugInfo(ref, "Intervention", ExtendedOrderEntryLogType.LISTENER_REMOVE);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public hasOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      res(this.orderEntryReferences.has(ref));
    });
  }

  /* ******************************************************* */
  /* VISIBILITY */

  private isVisible(interventionEvent: CubiqTS.InterventionEvent): boolean {
    let isVisible = false;
    if (interventionEvent instanceof CubiqTS.InterventionEventImpl) {
      let salesGroupId = interventionEvent.salesGroupId
        ? interventionEvent.salesGroupId.toString()
        : null;
      if (
        interventionEvent.orderData instanceof CubiqTS.OrderDataImpl &&
        interventionEvent.orderData.orderType === CubiqTS.OrderType.BENCHMARK
      ) {
        isVisible = this.activeUser.canSeeBmarkIntervention(
          interventionEvent.orderData,
          salesGroupId,
          interventionEvent.reason,
        );
      } else if (interventionEvent.fwdRollOrderData instanceof CubiqTS.FwdRollOrderDataImpl) {
        const fwdRollData = interventionEvent.fwdRollOrderData;
        return fwdRollData.orders.every((orderData) => {
          if (orderData != null && orderData.requestedOrderType == CubiqTS.RequestOrderType.BMARK) {
            return this.activeUser.canSeeBmarkIntervention(
              orderData,
              salesGroupId,
              interventionEvent.reason,
            );
          } else if (orderData != null) {
            return this.activeUser.canSeeOrder(orderData);
          }
        });
      } else {
        let entryUserCode =
          interventionEvent.orderData instanceof CubiqTS.OrderDataImpl
            ? interventionEvent.orderData.entryUserCode
            : null;
        let traderGroupId =
          interventionEvent.traderGroupId instanceof CubiqTS.Long
            ? interventionEvent.traderGroupId.toString()
            : null;
        let salesGroupId =
          interventionEvent.salesGroupId instanceof CubiqTS.Long
            ? interventionEvent.salesGroupId.toString()
            : null;

        isVisible = this.activeUser.canSeeIntervention(
          entryUserCode,
          traderGroupId,
          salesGroupId,
          interventionEvent.reason,
          interventionEvent.eventType,
        );
      }
    }
    return isVisible;
  }

  private isFixingRateVisible(interventionEvent: CubiqTS.InterventionEvent): boolean {
    if (
      interventionEvent instanceof CubiqTS.InterventionEventImpl &&
      interventionEvent.fixingRateRequirement != null
    ) {
      // Fixing rate visibility rules
      let traderGroupIds: CubiqTS.Long[] = interventionEvent.fixingRateRequirement.traderGroupIds;

      if (Array.isArray(traderGroupIds)) {
        let isFixingRateEvent: boolean =
          interventionEvent.eventType === CubiqTS.InterventionEventType.FIXING_RATE;
        let isTrader: boolean = this.activeUser.isTrader();
        let belongsToTraderGroup = traderGroupIds
          .map((a) => a.toString())
          .some((tgId: string) => this.activeUser.tradingGroupsIdSet.has(tgId));

        let canSeeCcyPair = this.activeUser.visibilityModel.canSeeCcyPair(
          interventionEvent.currencyPair,
        );
        if (!canSeeCcyPair && interventionEvent.fixingRateRequirement.visibleCcyPairs != null) {
          let visiblePairArray: string[] = interventionEvent.fixingRateRequirement.visibleCcyPairs;
          canSeeCcyPair = visiblePairArray.some((pair: string) =>
            this.activeUser.visibilityModel.canSeeCcyPair(pair),
          );
        }

        return isFixingRateEvent && isTrader && belongsToTraderGroup && canSeeCcyPair;
      }
    }

    return false;
  }

  protected keyProperty(): string {
    return "eventId";
  }

  /* ******************************************************* */
  /* NOTIFICATIONS */

  private convertEventTypeToSubType(eventType: CubiqTS.DIReason): BFXNotificationSubType {
    switch (eventType) {
      case CubiqTS.DIReason.ENTRY_CREDIT_FAIL:
      case CubiqTS.DIReason.ENTRY_CREDIT_PROXIMITY_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_ENTRY;
      case CubiqTS.DIReason.MODIFY_CREDIT_FAIL:
      case CubiqTS.DIReason.MODIFY_CREDIT_PROXIMITY_FAIL:
        return BFXNotificationSubType.MODIFY_FAIL_ENTRY;
      case CubiqTS.DIReason.FILL_CREDIT_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_FILL;
      case CubiqTS.DIReason.CREDIT_REJECT_FAIL:
      case CubiqTS.DIReason.CREDIT_REJECT_TIMEOUT:
        return BFXNotificationSubType.CREDIT_FAIL_REJECTED;
      case CubiqTS.DIReason.DAILY_REVAL_CREDIT_FAIL:
      case CubiqTS.DIReason.PROXIMITY_REVAL_CREDIT_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_REVAL;
      case CubiqTS.DIReason.FILL_NOT_RECEIVED:
        return BFXNotificationSubType.FILL_NOT_RECIEVED;
      case CubiqTS.DIReason.FILL_WITHELD:
        return BFXNotificationSubType.FILL_WITHHELD;
      case CubiqTS.DIReason.FORCE_TO_DI:
        return BFXNotificationSubType.CREDIT_FAIL_FORCE_DI;
      case CubiqTS.DIReason.CREDIT_CANCEL_FAIL:
      case CubiqTS.DIReason.CREDIT_CANCEL_TIMEOUT:
      case CubiqTS.DIReason.CREDIT_EXPIRY_FAIL:
      case CubiqTS.DIReason.CREDIT_EXPIRY_TIMEOUT:
        return BFXNotificationSubType.CREDIT_FAIL_CANCELEXPIRY;
      case CubiqTS.DIReason.FIXING_RATE_REQUIREMENT:
        return BFXNotificationSubType.FIXING_RATE_REQUIREMENT;
      case CubiqTS.DIReason.PORTFOLIO_FWD_ROLL:
      case CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_SAVED:
      case CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_AUTO_SAVED:
      case CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_ERROR:
        return BFXNotificationSubType.FWD_ROLL_PRICE;
    }
    return undefined;
  }

  /* ******************************************************* */
  /* MODEL UPDATES */

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(
    client: CubiqTS.ClientImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return (
      event.orderData && (event.orderData as CubiqTS.OrderDataImpl).clientCode == client.clientId
    );
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(
    account: CubiqTS.AccountImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return event.orderData && (event.orderData as CubiqTS.OrderDataImpl).accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(
    currency: CubiqTS.CurrencyImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return (
      event.orderData &&
      ((event.orderData as CubiqTS.OrderDataImpl).baseCode == currency.id ||
        (event.orderData as CubiqTS.OrderDataImpl).nonBaseCode == currency.id)
    );
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return (
      event.orderData &&
      (event.orderData as CubiqTS.OrderDataImpl).baseCode == currencyPair.base &&
      (event.orderData as CubiqTS.OrderDataImpl).nonBaseCode == currencyPair.nonBase
    );
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return this.compareIds(event.traderGroupId, traderGroup.groupId);
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    event: CubiqTS.InterventionEventImpl,
  ): boolean {
    return this.compareIds(event.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, event: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.compareIds(event.lockUserId, user.userId) ||
      (event.orderData &&
        this.compareIds((event.orderData as CubiqTS.OrderDataImpl).transferUserId, user.userId))
    );
  }

  private convertEventTypeToNotifType(
    eventType: CubiqTS.InterventionOperationType,
  ): BFXNotificationType {
    switch (eventType) {
      case CubiqTS.InterventionOperationType.NEW:
        return BFXNotificationType.DEALER_INTERVENTION;
    }
    return undefined;
  }

  /**
   * Get a client code from the fwdRollData. If any of the orders are privatised, then that
   * privatised client code is used, otherwise use the client code of the first order
   * @param fwdRollData
   * @returns
   */
  private getPrivatisedClientCode(fwdRollData: CubiqTS.FwdRollOrderData): string {
    const count = fwdRollData?.orders?.length ?? 0;
    let clientCode = count > 0 ? fwdRollData.orders[0].clientCode : "";

    for (let i = 1; i < count; i++) {
      const rollOrder = fwdRollData.orders[i];
      if (StaticDataPrivatizationType.hasPrivatization(rollOrder)) {
        clientCode = rollOrder.clientCode;
        break;
      }
    }
    return clientCode;
  }
}
